// src/components/clients/InvoicePdfModal.tsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Grid,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { refusalMessage } from "../../lib/api";
import type { ClientStageSync, Invoice, InvoiceStatus } from "./invoiceTypes";
import { formatDate } from "./helpers";
import {
  downloadInvoicePdf,
  updateInvoiceStatus,
} from "../../services/invoiceService";
import { parseCalendarDate } from "../../lib/calendarDate";

interface InvoicePdfModalProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  /** PKT-0916: `stageSync` says whether the backend moved the client's stage
   * (invoice -> paid) or left it and wrote a note (an un-pay). */
  onStatusChange?: (invoiceId: string, newStatus: string, stageSync?: ClientStageSync) => void;
}

const invoiceStatuses = [
  { value: "sent", label: "Sent", color: "#3b82f6", bgColor: "#3b82f620" },
  { value: "paid", label: "Paid", color: "#22c55e", bgColor: "#22c55e20" },
  {
    value: "overdue",
    label: "Overdue",
    color: "#ef4444",
    bgColor: "#ef444420",
  },
  {
    value: "cancelled",
    label: "Cancelled",
    color: "#64748b",
    bgColor: "#64748b20",
  },
];

const InvoicePdfModal: React.FC<InvoicePdfModalProps> = ({
  open,
  onClose,
  invoice,
  onStatusChange,
}) => {
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>("");

  // Load PDF data when modal opens
  useEffect(() => {
    if (open && invoice) {
      setCurrentStatus(invoice.status);
      loadPdfData();
    }
  }, [open, invoice]);

  useEffect(() => {
    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [pdfObjectUrl]);

  const loadPdfData = async () => {
    if (!invoice) return;

    setLoading(true);
    setError(null);
    setPdfObjectUrl(null);

    try {
      const blob = await downloadInvoicePdf(invoice.id);
      setPdfObjectUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      console.error("Error loading PDF:", err);
      setError(err.message || "Failed to load PDF data");
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  const statusInfo =
    invoiceStatuses.find((s) => s.value === currentStatus) ||
    invoiceStatuses[0];
  const isOverdue =
    currentStatus === "sent" && parseCalendarDate(invoice.due_date) < new Date();

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;

    setUpdating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // PKT-0911: through the backend, never a direct Supabase write. RLS let
      // a non-owner's update match zero rows with no error, and this modal
      // then showed PAID and logged a false activity. The backend checks
      // authority, 404s on a zero-row update, sets/clears paid_at, and writes
      // the activity row; this resolves only with the row in its NEW status.
      const updated = await updateInvoiceStatus(
        invoice.id,
        newStatus as InvoiceStatus
      );

      setCurrentStatus(updated.status);
      const sync = updated.client_stage_sync;
      setSuccessMessage(
        sync?.moved
          ? `Status updated to ${updated.status} · client moved to Payment Received`
          : sync?.note
          ? `Status updated to ${updated.status} · client stage left as is (note added)`
          : sync?.skipped_reason
          ? `Status updated to ${updated.status} · client stage NOT moved: ${sync.skipped_reason}`
          : `Status updated to ${updated.status}`
      );

      // Notify parent component
      if (onStatusChange) {
        onStatusChange(invoice.id, updated.status, sync);
      }

      // Clear success message after 2 seconds
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      console.error("Error updating invoice status:", err);
      setError(refusalMessage(err) || err?.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfObjectUrl) {
      setError("PDF not available for download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = pdfObjectUrl;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      setError("Failed to download PDF");
    }
  };

  const handleOpenInNewTab = () => {
    if (!pdfObjectUrl) {
      setError("PDF not available");
      return;
    }

    try {
      window.open(pdfObjectUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error opening PDF:", err);
      setError("Failed to open PDF in new tab");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          backgroundColor: "#0f172a",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "#1e293b", color: "white", pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <ReceiptIcon />
            <Box>
              <Typography variant="h6" component="span">
                {invoice.invoice_number}
              </Typography>
              <Chip
                size="small"
                label={statusInfo.label.toUpperCase()}
                sx={{
                  ml: 2,
                  height: 24,
                  fontWeight: 600,
                  backgroundColor: statusInfo.bgColor,
                  color: statusInfo.color,
                }}
              />
              {isOverdue && (
                <Chip
                  size="small"
                  icon={<WarningIcon sx={{ fontSize: 14 }} />}
                  label="OVERDUE"
                  sx={{
                    ml: 1,
                    height: 24,
                    fontWeight: 600,
                    backgroundColor: "#ef444420",
                    color: "#f87171",
                    "& .MuiChip-icon": { color: "#f87171" },
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ m: 2 }} icon={<CheckCircleIcon />}>
            {successMessage}
          </Alert>
        )}

        {/* Invoice Details Summary */}
        <Paper
          sx={{
            m: 2,
            mb: 0,
            p: 2,
            backgroundColor: "#1e293b",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Property
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#e2e8f0", fontWeight: 500 }}
                noWrap
              >
                {invoice.property_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Recipient
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#e2e8f0", fontWeight: 500 }}
                noWrap
              >
                {invoice.recipient_name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }} noWrap>
                {invoice.recipient_email}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Amount
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#22c55e", fontWeight: 700 }}
              >
                ${invoice.amount.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Due Date
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isOverdue ? "#f87171" : "#e2e8f0",
                  fontWeight: 500,
                }}
              >
                {formatDate(invoice.due_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", display: "block", mb: 0.5 }}
              >
                Update Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={currentStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updating}
                    sx={{
                      backgroundColor: "#334155",
                      color: "#e2e8f0",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#475569",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64748b",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#94a3b8",
                      },
                    }}
                  >
                    {invoiceStatuses.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: status.color,
                            }}
                          />
                          {status.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {updating && (
                  <CircularProgress size={20} sx={{ color: "#60a5fa" }} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* PDF Viewer */}
        <Box sx={{ flex: 1, m: 2, mt: 1, minHeight: 400 }}>
          {loading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#1e293b",
                borderRadius: 2,
              }}
            >
              <CircularProgress sx={{ color: "#60a5fa" }} />
              <Typography sx={{ mt: 2, color: "#94a3b8" }}>
                Loading PDF...
              </Typography>
            </Box>
          ) : pdfObjectUrl ? (
            <iframe
              src={pdfObjectUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 8,
                backgroundColor: "#fff",
              }}
              title={`Invoice ${invoice.invoice_number}`}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#1e293b",
                borderRadius: 2,
                border: "1px dashed #475569",
              }}
            >
              <ReceiptIcon sx={{ fontSize: 64, color: "#475569", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "#94a3b8" }}>
                PDF Not Available
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", mt: 1, textAlign: "center", px: 4 }}
              >
                The PDF for this invoice hasn't been generated yet.
                <br />
                This may happen for invoices created before PDF storage was
                enabled.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "#1e293b",
          borderTop: "1px solid #334155",
          px: 3,
          py: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: "100%" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Invoice Date: {formatDate(invoice.invoice_date)}
              {invoice.sent_at && ` • Sent: ${formatDate(invoice.sent_at)}`}
              {invoice.paid_at && ` • Paid: ${formatDate(invoice.paid_at)}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
              disabled={!pdfObjectUrl}
              sx={{
                color: "#94a3b8",
                borderColor: "#475569",
                "&:hover": {
                  borderColor: "#64748b",
                  backgroundColor: "#33415520",
                },
                "&:disabled": {
                  color: "#475569",
                  borderColor: "#334155",
                },
              }}
            >
              Open in New Tab
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!pdfObjectUrl}
              sx={{
                backgroundColor: "#3b82f6",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
                "&:disabled": {
                  backgroundColor: "#334155",
                },
              }}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePdfModal;
